// The countable outcome line for a publish — BL-1517, ADR 046 §7.
//
// This logging used to live inside `composite-publisher.ts`, which fanned one
// publish out to Supabase and Memorystore and emitted one line per transport.
// BL-1517 deleted the Supabase adapter, so the fan-out is gone — but the line is
// not: it is the sole input to the `realtime_publish_outcome` log-based metric,
// and deleting the composite without moving it would have taken the metric
// silent with no error anywhere.
//
// It is a decorator rather than something folded into the Memorystore adapter so
// the adapter keeps owning transport concerns and this keeps owning the metric's
// contract — and so the next transport gets the same observability by wrapping,
// not by re-implementing.

import type { RealtimePublisher } from '../../types/realtime/publisher';
import type { PublishResult } from '../../types/realtime/types';
import { redactTopicSecret } from '../../isomorphic/realtime/topics';

/** Options for {@link withPublishOutcomeLogging}. */
export interface PublishOutcomeLoggingOptions {
    /**
     * Label for the `transport` metric dimension, e.g. `memorystore`.
     *
     * Bounded by construction — it is a literal at the composition root, never
     * derived from a payload. A metric label must stay bounded (same constraint
     * the `cron_route` label satisfies in `infra/modules/monitoring/main.tf`).
     */
    transport: string;
}

/**
 * The message every outcome line carries.
 *
 * **Load-bearing string — changing it silently blinds the metric.** The
 * `realtime_publish_outcome` log-based metric in
 * `infra/modules/monitoring/main.tf` filters on it with an EXACT match (`=`, not
 * `=~`, so it cannot also swallow the adapters' `realtime publish failed` error
 * lines and double-count). Edit one and you must edit the other.
 */
const OUTCOME_MESSAGE = 'realtime publish outcome';

/**
 * Emit exactly ONE structured outcome line for a publish.
 *
 * **Why every outcome and not just failures.** Success was silent, which made
 * the question that matters unanswerable: is the bus carrying traffic at all,
 * and what is the `transport_unavailable` RATE? A rate needs a denominator, and
 * the denominator was never logged.
 *
 * The message string is a constant and the filter matches it EXACTLY, so this
 * cannot also catch the adapters' own `realtime publish failed` error lines —
 * those still fire underneath, and they are the ones carrying the stack. This
 * line is the countable one; theirs is the diagnosable one.
 *
 * `topic` rides in the body, never as a metric label — it is per-lot and
 * unbounded, and a metric label must stay bounded.
 *
 * It is logged in its REDACTED form. An `admin:` topic ends in an HMAC segment
 * that is the channel's access gate, not a name, so the raw string is a bearer
 * credential — see `redactTopicSecret`. The payload is never logged at all.
 *
 * @param args - The transport label, the publish arguments, and its outcome.
 * @sideEffect Writes one line to stdout, which Cloud Run ingests into Cloud
 *   Logging and the `realtime_publish_outcome` log-based metric counts.
 */
function logOutcome({
    transport,
    topic,
    event,
    outcome,
}: {
    transport: string;
    topic: string;
    event: string;
    outcome: PromiseSettledResult<PublishResult>;
}): void {
    // REDACTED, always. An `admin:` topic's trailing HMAC segment is the access
    // gate to that channel's confidential payload, so writing it raw here would
    // publish a bearer token to every reader of Cloud Logging on every publish.
    const context = { transport, topic: redactTopicSecret(topic), event };

    if (outcome.status === 'rejected') {
        // The adapter broke its non-throwing contract. Error passed
        // POSITIONALLY, not nested under a key — the boot console patch promotes
        // a third-arg Error into Cloud Logging's reserved `error.message` /
        // `error.stack` and auto-groups it in Error Reporting. Nested under a
        // plain key it is opaque JSON (structured-logging.md rule 1).
        //
        // Coerced to a real Error first. `outcome.reason` is `any`, and the
        // console patch only treats a positional argument as the reserved error
        // slot when it IS an Error — anything else gets appended onto the
        // message string instead. That would break the exact-match filter the
        // metric depends on and silently drop the occurrence, which is the same
        // missing-denominator failure this logging exists to fix.
        const reason =
            outcome.reason instanceof Error ? outcome.reason : new Error(String(outcome.reason));
        console.error(OUTCOME_MESSAGE, { ...context, result: 'threw' }, reason);
        return;
    }

    if (!outcome.value.success) {
        console.warn(OUTCOME_MESSAGE, { ...context, result: 'failure', code: outcome.value.code });
        return;
    }

    console.info(OUTCOME_MESSAGE, { ...context, result: 'success' });
}

/**
 * Wrap a publisher so every publish emits one countable outcome line.
 *
 * **Observes; never alters.** The wrapped publisher's `PublishResult` is
 * returned unchanged. Routes surface that result as a `warning` per
 * error-handling-chain.md, so a decorator that swallowed a failure would show
 * operators an all-clear on a broadcast that never went out.
 *
 * The one case it does change: a wrapped publisher that **throws** is converted
 * into a typed failure. `RealtimePublisher` implementations are contractually
 * non-throwing, but a publish fires post-commit on a request that has already
 * returned, so this does not rely on that contract being honoured — an
 * unhandled rejection there has no caller left to catch it.
 *
 * @param publisher - The transport to wrap.
 * @param options - The transport label the outcome line is attributed to.
 * @returns A RealtimePublisher with identical semantics, plus the outcome line.
 *
 * @sideEffect Emits one structured `realtime publish outcome` line per publish.
 *   Never throws.
 */
export function withPublishOutcomeLogging(
    publisher: RealtimePublisher,
    { transport }: PublishOutcomeLoggingOptions,
): RealtimePublisher {
    return {
        async publish(args): Promise<PublishResult> {
            // `allSettled` over a bare try/catch so a throw and a typed failure
            // reach `logOutcome` through the same shape — the three-way
            // success / failure / threw split is what the metric's `result`
            // label is, and collapsing two of them here would lose it.
            const [outcome] = await Promise.allSettled([publisher.publish(args)]);

            logOutcome({ transport, topic: args.topic, event: args.event, outcome });

            if (outcome.status === 'rejected') {
                // The caller's contract is a PublishResult, not an exception.
                // The `threw` line above already carried the reason and its stack.
                return { success: false, error: 'Publish failed', code: 'unknown' };
            }

            return outcome.value;
        },
    };
}
